import { useCallback, useState } from 'react'
import { supabase } from '../lib/supabase'
import { showToast } from './Toast'

// Stripe — l'état du lien profil ↔ client Stripe.
//
// Pourquoi cet écran existe. Relevé le 2026-10-12 : 72 profils, 11 avec un
// `stripe_customer_id`, et 54 factures Stripe parties vers 32 clients
// distincts. `send-invoice.js` créait le client et n'écrivait jamais son
// identifiant sur le profil. Or `updateByCustomer()` du webhook ne retrouve
// un profil QUE par ce champ : pour 24 personnes, aucun évènement
// d'abonnement n'atteignait leur profil — pas même le passage en `past_due`.
//
// La fuite est bouchée dans send-invoice.js. Cet écran sert au rattrapage de
// l'existant et à l'inventaire des doublons.
//
// Deux boutons, deux niveaux de risque :
//   « Run audit »  lecture seule, rien n'est écrit, toujours disponible
//   « Apply »      n'apparaît QUE s'il y a des liens sans ambiguïté, et
//                  demande une confirmation avant d'écrire
//
// Tout le travail est fait par la fonction Netlify, qui est seule à parler à
// Stripe et seule à produire le texte du rapport. Cet écran ne recalcule
// rien : il affiche `report.text` et lit `report.a_rattraper` pour savoir
// s'il doit proposer le second bouton. Un seul endroit produit le rapport.
export default function StripeAuditPanel() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [applying, setApplying] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')

  const run = useCallback(async (apply = false) => {
    setError('')
    if (apply) setApplying(true); else setLoading(true)
    try {
      const { data: s } = await supabase.auth.getSession()
      const token = s?.session?.access_token
      if (!token) throw new Error('Not signed in')

      const res = await fetch('/.netlify/functions/stripe-customers-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ apply, format: 'json' }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`)

      setReport(data)
      setConfirming(false)
      if (apply) {
        const n = data?.rattrapes?.length || 0
        const failed = data?.echecs_rattrapage?.length || 0
        showToast(
          failed
            ? `${n} profile(s) linked, ${failed} failed — see the report`
            : `${n} profile(s) linked`,
        )
      }
    } catch (err) {
      setError(err?.message || 'Audit failed')
    } finally {
      setLoading(false)
      setApplying(false)
    }
  }, [])

  const toLink = report?.a_rattraper?.length || 0
  const busy = loading || applying

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <h2 className="font-bricolage text-[16px] font-extrabold text-navy">
        Stripe — profile ↔ customer link
      </h2>
      <p className="mt-1 text-[12px] leading-relaxed text-ink-2">
        Checks every profile that has paid us against Stripe. A profile with no{' '}
        <code className="rounded bg-[#F3F4F6] px-1">stripe_customer_id</code> is invisible
        to the webhook: none of its subscription events reach it, not even the switch to
        past&nbsp;due.
      </p>
      <p className="mt-1 text-[12px] font-bold leading-relaxed text-navy">
        Running the audit writes nothing. Duplicates are never merged.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => run(false)}
          disabled={busy}
          className="rounded-lg bg-navy px-3 py-2 font-bricolage text-[12px] font-extrabold text-white shadow-sm disabled:opacity-50"
        >
          {loading ? 'Checking Stripe…' : report ? 'Run audit again' : 'Run audit'}
        </button>

        {/* Le second bouton n'existe que s'il y a vraiment quelque chose à
            écrire. Pas de bouton grisé : une action impossible n'a rien à
            faire à l'écran. */}
        {report && toLink > 0 && !confirming && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={busy}
            className="rounded-lg border-2 border-navy bg-white px-3 py-2 font-bricolage text-[12px] font-extrabold text-navy disabled:opacity-50"
          >
            Apply {toLink} link{toLink > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {confirming && (
        <div
          className="mt-3 rounded-[10px] p-3 text-[12px]"
          style={{ background: '#FEF9E7', border: '1px solid #F0DCA0', color: '#7A5E0C' }}
        >
          <div className="font-bricolage text-[13px] font-extrabold">
            Write {toLink} link{toLink > 1 ? 's' : ''} onto {toLink > 1 ? 'these profiles' : 'this profile'}?
          </div>
          <p className="mt-1 leading-relaxed">
            Only profiles whose email matches <strong>exactly one</strong> Stripe customer
            are touched. Nothing is merged, no Stripe customer is created, and an existing{' '}
            <code>stripe_customer_id</code> is never overwritten.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => run(true)}
              disabled={applying}
              className="rounded-md bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
            >
              {applying ? 'Writing…' : 'Yes, create the links'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={applying}
              className="rounded-md border border-[#D9C98A] px-3 py-1.5 text-[11px] font-extrabold disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          className="mt-3 rounded-[10px] p-3 text-[12px]"
          style={{ background: '#FDECEA', border: '1px solid #F5C6C2', color: '#8A2A22' }}
        >
          <span className="font-extrabold">Could not run the audit.</span> {error}
        </div>
      )}

      {report?.text && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-ink-2">
            <Pill label="To link" n={toLink} warn={toLink > 0} />
            <Pill label="Duplicates" n={report?.doublons_stripe?.length || 0} warn={(report?.doublons_stripe?.length || 0) > 0} />
            <Pill label="Mismatches" n={report?.incoherences?.length || 0} warn={(report?.incoherences?.length || 0) > 0} />
            <Pill label="Shared phones" n={report?.profils_partageant_un_telephone?.length || 0} warn={(report?.profils_partageant_un_telephone?.length || 0) > 0} />
            <Pill label="Already correct" n={report?.deja_corrects || 0} />
          </div>
          <pre className="mt-2 max-h-[60vh] overflow-auto rounded-lg bg-[#0B2038] p-3 text-[11px] leading-relaxed text-[#E8EDF2]">
            {report.text}
          </pre>
          <p className="mt-2 text-[11px] text-ink-2">
            Duplicate Stripe customers are merged by hand in the Stripe dashboard — never
            from here, because merging cannot be undone.
          </p>
        </>
      )}
    </div>
  )
}

function Pill({ label, n, warn = false }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-extrabold ${
        warn ? 'bg-[#FEF9E7] text-[#7A5E0C]' : 'bg-[#F3F4F6] text-ink-2'
      }`}
    >
      {label}: {n}
    </span>
  )
}
